import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Result, Button, Checkbox, message, Row, Col } from 'antd';
import axios from '../../config/axiosInstance';
import { useAuth } from '../../context/AuthContext';

const TermsModal = ({ open, deviceConfigId, onSuccess, onCancel }) => {
    const { user } = useAuth();

    const [loading, setLoading] = useState(false);
    const [checked, setChecked] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [countdown, setCountdown] = useState(3);

    /* Visible number/BD*/
    const displayName = useMemo(() => {
        const full = `${user?.firstname || ''} ${user?.lastname || ''}`.trim();
        return full || user?.name || 'anonymous';
    }, [user]);

    /* Frozen date-time*/
    const timestamp = useMemo(() => new Date(), [open]);
    const dateStr = timestamp.toLocaleDateString('es-ES');
    const timeStr = timestamp.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

    /* Countdown after acceptance */
    useEffect(() => {
        if (!accepted) return;
        setCountdown(3);
        const timer = setInterval(() => {
            setCountdown((c) => {
                if (c <= 1) {
                    clearInterval(timer);
                    onSuccess();
                    return 0;
                }
                return c - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [accepted]);

    const handleAccept = async () => {
        try {
            setLoading(true);
            await axios.put(`/deviceConfigs/${deviceConfigId}`, {
                termsAccepted: true,
                termsAcceptedBy: displayName,
                termsAcceptedAt: new Date(),
            });
            message.success('Accepted use terms');
            setAccepted(true);
        } catch {
            message.error('Could not save the acceptance of the terms of use');
        } finally {
            setLoading(false);
        }
    };

    /* === Render === */
    return (
        <Modal open={open} onCancel={onCancel} footer={null} centered destroyOnClose width={800}>
            {!accepted ? (
                <>
                    <Result
                        status="warning"
                        title="Billing conditions"
                        subTitle={
                            <>
                                When assigning devices to a configuration, usage will be <strong>recorded</strong> and may be subject to applicable usage agreements.<br/><br/>
                                You may incur charges for each device used during an event.<br/><br/>
                                If the devices are rented from us,
                                usage will also be recorded for documentation purposes only, and no separate usage charges will apply.<br/><br/>
                                If you have any questions, please contact your Account Manager.
                            </>
                        }
                    />
                    <Row justify="center">
                        <Checkbox
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                            style={{ marginBottom: 30 }}
                        >
                            I confirm that I, <strong>{displayName}</strong>, I agree to the terms on <strong>{dateStr}</strong> at <strong>{timeStr}</strong>.
                        </Checkbox>
                    </Row>
                    <Row justify="center" gutter={16}>
                        <Col>
                            <Button onClick={onCancel}>Cancel</Button>
                        </Col>
                        <Col>
                            <Button type="primary" loading={loading} onClick={handleAccept} disabled={!checked}>
                                Accept and continue
                            </Button>
                        </Col>
                    </Row>
                </>
            ) : (
                <Result
                    status="success"
                    title="Terms accepted!"
                    subTitle={`Opening device transfer in ${countdown} s…`}
                />
            )}
        </Modal>
    );
};

export default TermsModal;
